import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import styled, { keyframes } from 'styled-components'
import { Overlay, Panel, TitleRow, H1, CloseBtn, Section } from '../styles/TransactionDetailStyle'
import type { Country } from '../../types/ApiTypes'
import { systemService } from '../services/SystemService'
import { giftcardService } from '../services/GiftcardService'

interface AddGiftcardSidebarProps {
   open: { open: boolean; id: string; regions: string[] }
   onClose: () => void
   onSave: () => void
}

const spin = keyframes`
   to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
   width: 16px;
   height: 16px;
   border: 2px solid #E7E7E9;
   border-top-color: #C02236;
   border-radius: 50%;
   animation: ${spin} 0.6s linear infinite;
`;

export default function AddGiftcardSidebar({ open, onClose, onSave }: AddGiftcardSidebarProps) {
   const [regions, setRegions] = useState<string[]>([])
   const [openRegion, setOpenRegion] = useState(false)
   const anchorRef = useRef<HTMLButtonElement | null>(null)
   const [saving, setSaving] = useState(false)
   const [countries, setCountries] = useState<Country[]>([])

   useEffect(() => {
      loadCountries()
   }, [])

   const loadCountries = async () => {
      try {
         const response = await systemService.getCountries()
         if (response.status === 'success' && response.data) {
            setCountries(response.data)
         }
      } catch (error) {
         console.error('Failed to load countries:', error)
      }
   }

   useEffect(() => {
      setRegions(open.regions)

      function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
      if (open.open) window.addEventListener('keydown', onEsc)
      return () => window.removeEventListener('keydown', onEsc)
   }, [open, onClose])

   if (!open.open) return null

   const getCountryName = (code: string) => countries.find(c => c.code === code)?.name || code

   const toggleRegion = (code: string) => setRegions(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code])

   const handleSave = async () => {
      setSaving(true)
      try {
         const response = await giftcardService.updateGiftcardRegions(open.id, { countryCodes: regions })
         if (response.status === 'success') {
            onSave()
         }
      } catch (error) {
         console.error('Failed to update regions:', error)
      } finally {
         setSaving(false)
      }
   }

   return (
      <Overlay onClick={onClose}>
         <Panel onClick={e => e.stopPropagation()}>
            <TitleRow>
               <H1>Edit Region</H1>
               <CloseBtn onClick={onClose}><Icon icon="material-symbols:close" width={20} /></CloseBtn>
            </TitleRow>

            {/* <Section>
               <div className='text-[12px] tracking-tight text-[#9C9C9F] mb-2'>Card logo</div>
               <div className='flex items-center gap-3 mb-2'>
                  {logo &&
                      <div className='w-8 h-8 rounded-full bg-[#101018] flex items-center justify-center overflow-hidden'>
                      {logo ? <img src={logo} alt='logo' className='w-full h-full object-cover' /> : <Icon icon='mdi:image-outline' className='text-white' width={16} />}
                      </div>
                  }
                  <label className='px-3 py-1.5 text-xs rounded-md border border-[#E7E7E9] bg-white cursor-pointer hover:bg-[#F9F9FA]'>
                     <input type='file' accept='image/png,image/svg+xml' className='hidden' onChange={e => {
                        const f = e.target.files?.[0]; if (!f) return; const url = URL.createObjectURL(f); setLogo(url);
                     }} />
                     Upload image
                  </label>
                  <button className='px-3 py-1.5 text-xs rounded-md border border-[#E7E7E9] bg-white hover:bg-[#F9F9FA]'>Select logo</button>
               </div>
               <div className='text-[11px] text-[#B3B3B6]'>Recommended: PNG or SVG, min 200x200px</div>
            </Section> */}

            {/* <Section>
               <div className='text-[12px] tracking-tight text-[#9C9C9F] mb-2'>Card name</div>
               <input value={name} onChange={e => setName(e.target.value)} placeholder='Starbucks'
                  className='w-full h-9 px-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#C02236] not-placeholder-shown:font-medium' />
            </Section> */}

            <Section>
               <div className='text-[12px] tracking-tight text-[#9C9C9F] mb-2'>Supported regions</div>
               <div className='flex flex-wrap gap-2 mb-2'>
                  {regions.map(r => (
                     <span key={r} className='px-2 py-1 text-[11px] rounded-full bg-[#F1F1F3] text-[#6D6D72]'>{getCountryName(r)}</span>
                  ))}
               </div>
               <div className='relative'>
                  <button ref={anchorRef} onClick={() => setOpenRegion(o => !o)} className='w-full h-9 px-3 rounded-md border border-[#E7E7E9] bg-white flex items-center justify-between text-sm hover:bg-[#F9F9FA]'>
                     <span className='text-[#9C9C9F]'>{regions.length ? 'Edit regions' : 'Select the supported regions'}</span>
                     <Icon icon='solar:alt-arrow-down-linear' width={16} className='text-[#B3B3B6]' />
                  </button>
                  {openRegion && (
                     <div className='absolute z-40 top-[42px] left-0 right-0'>
                        <RegionDropdown
                           open={openRegion}
                           onClose={() => setOpenRegion(false)}
                           anchorRef={anchorRef}
                           value={regions}
                           onToggle={toggleRegion}
                           countries={countries}
                        />
                     </div>
                  )}
               </div>
            </Section>

            <div className='mt-auto flex items-center justify-between'>
               <button onClick={onClose} className='px-4 py-2 rounded-full text-sm font-semibold bg-[#F5F5F6] text-[#3F3F45] hover:bg-[#EDEDEE] cursor-pointer' disabled={saving}>Cancel</button>
               <button onClick={handleSave} className='px-4 py-2 rounded-full text-sm font-semibold bg-[#C02236] text-white hover:bg-[#9a1a2a] cursor-pointer flex items-center gap-2' disabled={saving}>
                  {saving && <Spinner />}
                  Save
               </button>
            </div>
         </Panel>
      </Overlay>
   )
}

function RegionDropdown({ open, onClose, anchorRef, value, onToggle, countries }: { open: boolean; onClose: () => void; anchorRef: React.RefObject<HTMLButtonElement | null>; value: string[]; onToggle: (code: string) => void; countries: Country[] }) {
   const wrapRef = useRef<HTMLDivElement | null>(null)
   const [q, setQ] = useState('')

   useEffect(() => {
      function handle(e: MouseEvent) {
         if (!open) return
         if (wrapRef.current && !wrapRef.current.contains(e.target as Node) && anchorRef.current && !anchorRef.current.contains(e.target as Node)) onClose()
      }
      window.addEventListener('mousedown', handle)
      return () => window.removeEventListener('mousedown', handle)
   }, [open, onClose, anchorRef])

   const data = useMemo(() => countries.filter(c => c.name.toLowerCase().includes(q.toLowerCase()) || c.code.toLowerCase().includes(q.toLowerCase())), [countries, q])

   return (
      <div ref={wrapRef} className='bg-white border border-[#EDEDEE] rounded-lg shadow-[0_4px_6px_-2px_rgba(16,24,40,0.04),0_14px_16px_-2px_rgba(16,24,40,0.08)] p-2'>
         <div className='flex items-center gap-2 px-2 py-2 border-b border-[#F5F5F6]'>
            <Icon icon='solar:magnifer-linear' width={14} className='text-[#CACACC]' />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder='Search countries...' className='flex-1 text-sm outline-none' />
         </div>
         <div className='max-h-56 overflow-auto'>
            {data.length === 0 ? (
               <div className='text-center py-4 text-sm text-[#B3B3B6]'>No countries found</div>
            ) : (
               data.map(c => {
                  const checked = value.includes(c.code)
                  return (
                     <label key={c.code} className='flex items-center gap-3 px-2 py-2 hover:bg-[#F9F9FA] cursor-pointer text-sm'>
                        <input type='checkbox' checked={checked} onChange={() => onToggle(c.code)} className='accent-[#C02236]' />
                        <span>{c.name}</span>
                     </label>
                  )
               })
            )}
         </div>
         <div className='flex items-center gap-2 px-2 py-2 border-t border-[#F5F5F6] text-[12px] text-[#B3B3B6]'>
            <Icon icon='solar:checklist-linear' width={14} />
            <span>{value.length || 'No'} countr{value.length === 1 ? 'y' : 'ies'} selected</span>
         </div>
      </div>
   )
}
